import { useTranslation } from 'react-i18next'

import { formatSupplierBillingAddress } from '@/modules/requisitions/utils/formatSupplierBillingAddress'
import { Accordion } from '@/modules/shared/components/accordion/Accordion'
import { useGetProductModalSupplierDetails } from '@/modules/shared/components/product-modal/hooks/useGetProductModalSupplierDetails'
import { QueryResult } from '@/modules/shared/components/query-result/QueryResult'
import SupplierTags from '@/modules/shared/components/supplier-tags/SupplierTags'
import { TileDetailsTableItem } from '@/modules/shared/components/tile/tile-items/TileDetailsTableItem'

interface ProductModalSupplierDetailsProps {
  tradeRelationshipId: number
}

export default function ProductModalSupplierDetails({ tradeRelationshipId }: ProductModalSupplierDetailsProps) {
  const { t } = useTranslation()
  const { fetchProductModalSupplierDetails, supplier, loadingStates, error } = useGetProductModalSupplierDetails({
    tradeRelationshipId,
  })

  // Type guard to ensure we have a Supplier with all the fields
  const isSupplier = supplier && '__typename' in supplier && supplier.__typename === 'Supplier'
  const supplierDetails = isSupplier ? supplier : null
  const {
    name,
    telephone,
    email,
    billingAddressLine1,
    billingAddressLine2,
    billingAddressCity,
    billingAddressStateProvince,
    billingAddressPostalCode,
    supplierRelationship,
  } = supplierDetails || {}

  const handleAccordionOpen = () => {
    if (!supplier) {
      fetchProductModalSupplierDetails()
    }
  }

  return (
    <Accordion
      title={t('general.supplierDetails', 'Supplier Details')}
      buttonClassName="px-3 h-11 mt-2.5"
      titleClassName="font-bold"
      onOpen={handleAccordionOpen}
    >
      <QueryResult loading={loadingStates.loading} error={error}>
        <section data-testid="supplier-details" className="mb-4 mt-2 border">
          <TileDetailsTableItem
            title={t('general.supplier', 'Supplier')}
            value={name}
            tooltip={t('supplierDetails.supplierDescription', 'The name of the Supplier.')}
          />
          <TileDetailsTableItem
            title={t('general.headquarters', 'Headquarters')}
            value={
              supplierDetails &&
              formatSupplierBillingAddress({
                billingAddressLine1: billingAddressLine1 || null,
                billingAddressLine2: billingAddressLine2 || null,
                billingAddressCity: billingAddressCity || null,
                billingAddressStateProvince: billingAddressStateProvince || null,
                billingAddressPostalCode: billingAddressPostalCode || null,
              })
            }
            tooltip={t('supplierDetails.headquartersDescription', 'The main address of the Supplier.')}
          />
          <TileDetailsTableItem
            title={t('general.contactPhone', 'Contact Phone')}
            value={telephone}
            tooltip={t('supplierDetails.phoneDescription', 'The contact phone number provided by the Supplier.')}
          />
          <TileDetailsTableItem
            title={t('general.contactEmail', 'Contact Email')}
            value={email}
            tooltip={t('supplierDetails.emailDescription', 'The contact email address provided by the Supplier.')}
          />
          <TileDetailsTableItem
            title={t('general.supplierTags', 'Supplier Tags')}
            value={<SupplierTags preferredSupplier={!!supplierRelationship?.preferredSupplier} />}
            tooltip={t(
              'supplierDetails.tagsDescription',
              'The tags that your Organisation has tagged this Supplier with.'
            )}
          />
        </section>
      </QueryResult>
    </Accordion>
  )
}
